import { BASE_API_URL, OKTA_DOMAIN, OKTA_KEY } from "lib/constants";
import { RecaptchaError, captchaToken } from "lib/recaptcha";

import axios from "axios";
import { v4 } from "uuid";

export class SessionError extends Error {
  constructor(...a) {
    super(...(a.length ? a : [SessionError.name]));
    this.name = "SessionError";
  }
}

/**
 * @return {Promise<{ session_id: string, okta_id: string } | undefined>}
 * WARN: -- DOES NOT WORK WITH THIRD PARTY COOKIE DISABLED
 *          SAFARI BLOCKS THIRD PARTY COOKIES BY DEFAULT
 */
export const currentSession = async () => {
  const uri = new URL(`https://${OKTA_DOMAIN}/api/v1/sessions/me`);
  const { status, data } = await axios.get(uri, {
    withCredentials: true,
    validateStatus: (stat) =>
      stat >= 200 && stat < 500 && stat !== 400 && stat !== 422 && stat !== 422,
  });
  if (status === 404) {
    return undefined;
  } else {
    const { id: session_id, userId: okta_id } = data;
    return { session_id, okta_id };
  }
};

/**
 * @param {{ next: URL, replace: bool }}
 * @return {Promise<never>}
 *
 * Need to redirect to kill session, due to third party cookie blocking
 */
export const logout = async ({ next, replace = false }) => {
  const params = new URLSearchParams({ fromURI: new URL(next) });
  const uri = new URL(`https://${OKTA_DOMAIN}/login/signout?${params}`);
  if (replace) {
    globalThis.location.replace(uri);
  } else {
    globalThis.location.href = uri;
  }
};

/**
 * @param {{ username: string, password: string }}
 * @return {Promise<{ sessionToken: string, _embedded: { user: { id: string, profile: { firstName: string, lastName: string }}}}>}
 */
export const oktaUserInfo = async ({ username, password }) => {
  const { status, data } = await axios.post(
    new URL(`https://${OKTA_DOMAIN}/api/v1/authn`),
    { username: username.toLowerCase(), password },
    {
      validateStatus: (stat) =>
        stat >= 200 && stat < 500 && stat !== 400 && stat !== 422,
    }
  );
  if (status === 401) {
    const msg =
      "The information you provided does not match our records. Please re-enter your login information below";
    throw new Error(msg);
  } else {
    return data;
  }
};

/**
 * @return {Promise<{ sessionId: string, hashed_email: string, gf_account_types: string[], okta_groups: string[] } | undefined>}
 */
export const authStatusViaOktaSession = async () => {
  const [session, recaptcha] = await Promise.all([
    currentSession(),
    captchaToken({ action: "auth" }),
  ]);
  const { session_id, okta_id } = session ?? {};

  if (!session_id) {
    return undefined;
  } else {
    const { status, data } = await axios.patch(
      `${BASE_API_URL}/auth/user_info_via_okta_id`,
      {
        session_id,
        okta_id,
        recaptcha,
      },
      {
        validateStatus: (stat) =>
          stat >= 200 && stat < 500 && stat !== 400 && stat !== 422,
      }
    );
    if (status === 401) {
      throw new RecaptchaError();
    } else if (status === 404) {
      return undefined;
    } else {
      const { hashed_email, okta_id, gf_account_types, okta_groups } = data;
      return {
        session_id,
        okta_id,
        hashed_email,
        gf_account_types,
        okta_groups,
      };
    }
  }
};

/**
 * @param {{ username: string }}
 * @return {Promise<{ hashed_email: string, gf_account_types: string[], okta_groups: string[] } | undefined>}
 */
export const authStatusViaLogin = async ({ username }) => {
  const recaptcha = await captchaToken({ action: "auth" });
  const { status, data } = await axios.patch(
    `${BASE_API_URL}/auth/user_info_via_login`,
    {
      username,
      recaptcha,
    },
    {
      validateStatus: (stat) =>
        stat >= 200 && stat < 500 && stat !== 400 && stat !== 422,
    }
  );
  if (status === 401) {
    throw new RecaptchaError();
  } else if (status === 404) {
  } else {
    return data;
  }
};

/**
 * @param {{ next: URL, gtmEvent: string }}
 * @return {URL}
 */
export const postLoginGTM = ({ next, gtmEvent }) => {
  const params = new URLSearchParams({ next, gtmEvent });
  return new URL(`/omniauth/redirect?${params}`, next.origin);
};

/**
 * @param {{ user_type: "vendor" | "customer", username: string, password: string, next: URL, gtmEvent: string }}
 * @return {Promise<never>}
 */
export const loginViaEmail = async ({
  user_type,
  username,
  password,
  next: auth_origin_url,
  gtmEvent,
}) => {
  const { sessionToken } = await oktaUserInfo({ username, password });

  const authZ = new URL(`https://${OKTA_DOMAIN}/oauth2/default/v1/authorize`);
  const redirect_uri = `${globalThis.location.origin}/omniauth/okta/callback`;
  const state = btoa(
    JSON.stringify({
      gtmEvent,
      user_type,
      auth_origin_url,
      current_uri: globalThis.location.href,
    })
  );

  const params = new URLSearchParams({
    redirect_uri,
    client_id: OKTA_KEY,
    response_type: "code",
    scope: "openid profile email",
    sessionToken,
    state,
    nonce: v4(),
  });
  const next = new URL(`${authZ}?${params}`);
  await logout({ next });
};

/**
 * @param {{ user_type: "vendor" | "customer", session_id: string, okta_id: string, next: URL, gtmEvent: string }}
 * @return {Promise<never>}
 */
export const loginViaSession = async ({
  user_type,
  session_id,
  okta_id,
  next,
  gtmEvent,
}) => {
  const recaptcha = await captchaToken({ action: "auth" });
  const ut = { vendor: "provider" }[user_type] ?? user_type;
  const current_uri = new URL(
    `${globalThis.location.origin}/${ut}/auth?${new URLSearchParams({ next })}`
  );
  const auth_origin_url = postLoginGTM({ next, gtmEvent });
  const params = new URLSearchParams({
    recaptcha,
    user_type,
    current_uri,
    session_id,
    okta_id,
    auth_origin_url,
  });
  globalThis.location.replace(
    new URL(`${BASE_API_URL}/auth/login_via_session?${params}`)
  );
};

/**
 * @param {{ user_type: "vendor" | "customer", username: string, first_name: string, last_name: string, phone_number: string | undefined, password: string, success_url: URL, allow_marketing_emails: boolean, next: URL }}
 * @return {Promise<never>}
 */
export const signupViaNew = async ({
  user_type,
  username,
  first_name,
  last_name,
  phone_number,
  password,
  allow_marketing_emails,
  next,
  success_url,
}) => {
  const recaptcha = await captchaToken({ action: "auth" });
  const {
    status,
    data: { error },
  } = await axios.post(
    `${BASE_API_URL}/${user_type}_auth`,
    {
      recaptcha,
      user_type,
      email: username,
      first_name,
      last_name,
      phone_number,
      password,
      allow_marketing_emails,
      confirm_success_url: `${success_url}`,
    },
    {
      validateStatus: (stat) => stat >= 200 && stat < 500 && stat !== 400,
    }
  );

  if (status === 401) {
    throw new RecaptchaError();
  } else if (error) {
    throw new Error(error);
  } else {
    await loginViaEmail({
      user_type,
      username,
      password,
      next,
      gtmEvent: "oauth-sign-up",
    });
  }
};

/**
 * @param {{ user_type: "vendor" | "customer", session_id: string, okta_id: string, success_url: URL, allow_marketing_emails: boolean, next: URL }}
 * @return {Promise<never>}
 */
export const signupViaSession = async ({
  user_type,
  session_id,
  okta_id,
  success_url: confirm_success_url,
  allow_marketing_emails,
  next,
}) => {
  const recaptcha = await captchaToken({ action: "auth" });
  const {
    status,
    data: { error },
  } = await axios.post(
    `${BASE_API_URL}/${user_type}_auth`,
    {
      recaptcha,
      user_type,
      session_id,
      okta_id,
      allow_marketing_emails,
      confirm_success_url,
    },
    {
      validateStatus: (stat) =>
        stat >= 200 && stat < 500 && stat !== 400 && stat !== 422,
    }
  );

  if (status === 401) {
    throw new RecaptchaError();
  } else if (status === 410) {
    throw new SessionError();
  } else if (error) {
    throw new Error(error);
  } else {
    const fromSignup = true;
    await loginViaSession({
      user_type,
      session_id,
      okta_id,
      next,
      gtmEvent: "oauth-sign-up",
    });
  }
};

/**
 * @param {{ user_type: "vendor" | "customer", username: string, password: string, success_url: URL, allow_marketing_emails: boolean, next: URL }}
 * @return {Promise<never>}
 */
export const signupViaOkta = async ({
  user_type,
  username,
  password,
  allow_marketing_emails,
  success_url: confirm_success_url,
  next,
}) => {
  const [
    {
      _embedded: {
        user: { id: okta_id },
      },
    },
    recaptcha,
  ] = await Promise.all([
    oktaUserInfo({ username, password }),
    captchaToken({ action: "auth" }),
  ]);

  const {
    status,
    data: { error },
  } = await axios.post(
    `${BASE_API_URL}/${user_type}_auth`,
    {
      recaptcha,
      user_type,
      okta_id,
      allow_marketing_emails,
      confirm_success_url,
    },
    {
      validateStatus: (stat) =>
        stat >= 200 && stat < 500 && stat !== 400 && stat !== 422,
    }
  );

  if (status === 401) {
    throw new RecaptchaError();
  } else if (error) {
    throw new Error(error);
  } else {
    await loginViaEmail({
      user_type,
      username,
      password,
      next,
      gtmEvent: "oauth-sign-up",
    });
  }
};

/**
 * @param {string} error
 * @return {URL}
 */
export const fromRedirectError = ({ error, state }) => {
  const { current_uri } = JSON.parse(atob(state));
  const uri = new URL(current_uri);
  const params = new URLSearchParams({
    ...Object.fromEntries(uri.search),
    error,
  });
  return new URL(`${uri.origin}?${params}`);
};

/**
 * @param {{ code: string, state: string }}
 * @return {Promise<never>}
 */
export const fromRedirect = async ({ code, state }) => {
  const payload = JSON.parse(atob(state));
  const { user_type, auth_origin_url: next, current_uri, gtmEvent } = payload;
  const auth_origin_url = postLoginGTM({ next: new URL(next), gtmEvent });
  const params = new URLSearchParams({
    user_type,
    auth_origin_url,
    current_uri,
    state,
    code,
  });
  globalThis.location.replace(
    new URL(`${BASE_API_URL}/omniauth/okta/callback?${params}`)
  );
};
